import { useState, useRef, useEffect, useCallback } from 'react';
import { Scanner } from '@yudiel/react-qr-scanner';
import Peer, { DataConnection } from 'peerjs';
import './Components.css';

const MAX_WORKERS = 3; // Max parallel transfers

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected';
type FileTransferStatus = 'queued' | 'transferring' | 'completed' | 'failed' | 'paused';

interface FileTransferState {
    file: File;
    id: string;
    status: FileTransferStatus;
    progress: number;
    transferredBytes: number;
    speed: number;
    eta: number;
    startTime: number | null;
    error?: string;
}

interface TransferWorker {
    id: string;
    isBusy: boolean;
    chunkOffset: number;
    lastChunkTime: number | null;
}

interface SenderProps {
    goBack: () => void;
}

const Sender = ({ goBack }: SenderProps) => {
    const [files, setFiles] = useState<File[]>([]);
    const [fileTransfers, setFileTransfers] = useState<FileTransferState[]>([]);
    const [manualPeerId, setManualPeerId] = useState('');
    const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
    const [conn, setConn] = useState<DataConnection | null>(null);
    const [isTransferring, setIsTransferring] = useState(false);
    const [overallProgress, setOverallProgress] = useState({ completed: 0, total: 0, totalBytes: 0, transferredBytes: 0 });
    
    const peerRef = useRef<Peer | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const workersRef = useRef<Map<string, TransferWorker>>(new Map());
    const transferQueueRef = useRef<string[]>([]);

    useEffect(() => {
        const peer = new Peer({
            host: 'local-transfer-server.onrender.com',
            secure: true,
        });
        peerRef.current = peer;

        return () => {
            peer.destroy();
        };
    }, []);

    const updateTransferState = useCallback((transferId: string, updates: Partial<FileTransferState>) => {
        setFileTransfers(prev =>
            prev.map(ft => (ft.id === transferId ? { ...ft, ...updates } : ft))
        );
    }, []);

    useEffect(() => {
        const newTransfers = files
            .filter(file => !fileTransfers.some(ft => ft.file.name === file.name && ft.file.size === file.size))
            .map(file => ({
                file,
                id: `${file.name}-${file.size}-${Date.now()}`,
                status: 'queued' as FileTransferStatus,
                progress: 0,
                transferredBytes: 0,
                speed: 0,
                eta: 0,
                startTime: null,
            }));

        if (newTransfers.length > 0) {
            setFileTransfers(prev => [...prev, ...newTransfers]);
        }
    }, [files, fileTransfers]);

    useEffect(() => {
        const totalBytes = fileTransfers.reduce((sum, ft) => sum + ft.file.size, 0);
        const transferredBytes = fileTransfers.reduce((sum, ft) => sum + ft.transferredBytes, 0);
        const completed = fileTransfers.filter(ft => ft.status === 'completed').length;

        setOverallProgress({
            completed,
            total: fileTransfers.length,
            totalBytes,
            transferredBytes,
        });

        if (isTransferring && completed === fileTransfers.length && fileTransfers.length > 0) {
            setIsTransferring(false);
            console.log('All file transfers completed');
        }
    }, [fileTransfers, isTransferring]);

    const connectToPeer = (peerId: string) => {
        if (!peerRef.current) return;
        setConnectionStatus('connecting');
        try {
            const connection = peerRef.current.connect(peerId);
            setConn(connection);

            connection.on('open', () => setConnectionStatus('connected'));
            connection.on('data', (data) => handleReceivedData(data as any, connection));
            connection.on('close', () => {
                setConnectionStatus('disconnected');
                setIsTransferring(false);
                workersRef.current.forEach(worker => {
                    updateTransferState(worker.id, { status: 'failed', error: 'Connection closed' });
                });
            });
            connection.on('error', (error) => {
                setConnectionStatus('disconnected');
                setIsTransferring(false);
                workersRef.current.forEach(worker => {
                    updateTransferState(worker.id, { status: 'failed', error: error.message });
                });
            });
        } catch (error) {
            console.error('Error creating connection:', error);
            setConnectionStatus('disconnected');
        }
    };

    const startNextTransfer = useCallback((connection: DataConnection) => {
        const availableWorker = Array.from(workersRef.current.values()).find(w => !w.isBusy);
        if (!availableWorker) {
            console.log('No available workers');
            return;
        }

        const nextTransferId = transferQueueRef.current.shift();
        if (!nextTransferId) {
            console.log('No more files in queue');
            return;
        }
        
        setFileTransfers(prevFileTransfers => {
            const transfer = prevFileTransfers.find(ft => ft.id === nextTransferId);
            if (transfer) {
                console.log(`Assigning file ${transfer.file.name} to worker`);
                availableWorker.isBusy = true;
                availableWorker.id = transfer.id;

                updateTransferState(transfer.id, { status: 'transferring', startTime: Date.now() });
                
                try {
                    connection.send({
                        type: 'metadata',
                        payload: {
                            transferId: transfer.id,
                            name: transfer.file.name,
                            size: transfer.file.size,
                        },
                    });
                } catch (error) {
                    updateTransferState(transfer.id, { status: 'failed', error: 'Failed to send metadata' });
                    availableWorker.isBusy = false;
                }
            }
            return prevFileTransfers;
        });
    }, [updateTransferState, fileTransfers]);

    const sendChunk = useCallback((worker: TransferWorker, transfer: FileTransferState, connection: DataConnection) => {
        const file = transfer.file;
        const chunkSize = 64 * 1024;
        const slice = file.slice(worker.chunkOffset, worker.chunkOffset + chunkSize);
        const reader = new FileReader();

        reader.onload = (e) => {
            if (!e.target?.result) return;
            const chunk = e.target.result as ArrayBuffer;
            
            try {
                connection.send({ type: 'chunk', payload: { transferId: worker.id, chunk } });
                
                const currentTime = Date.now();
                const timeElapsed = worker.lastChunkTime ? (currentTime - worker.lastChunkTime) / 1000 : 0;
                const speed = timeElapsed > 0 ? chunk.byteLength / timeElapsed : 0;
                worker.lastChunkTime = currentTime;
                worker.chunkOffset += chunk.byteLength;

                const progress = Math.round((worker.chunkOffset / file.size) * 100);
                const eta = speed > 0 ? (file.size - worker.chunkOffset) / speed : 0;

                updateTransferState(worker.id, {
                    progress,
                    transferredBytes: worker.chunkOffset,
                    speed,
                    eta,
                });

                if (worker.chunkOffset < file.size) {
                    sendChunk(worker, transfer, connection);
                } else {
                    updateTransferState(worker.id, { status: 'completed', progress: 100 });
                    worker.isBusy = false;
                    worker.chunkOffset = 0;
                    worker.lastChunkTime = null;
                    startNextTransfer(connection);
                }
            } catch (error) {
                updateTransferState(worker.id, { status: 'failed', error: error instanceof Error ? error.message : 'Unknown error' });
                worker.isBusy = false;
            }
        };
        reader.readAsArrayBuffer(slice);
    }, [updateTransferState]);

    const handleReceivedData = (data: { type: string; payload: any }, connection: DataConnection) => {
        console.log('Sender received data:', data);
        if (data.type === 'transfer-accept') {
            console.log('Receiver accepted transfer, starting workers...');
            startNextTransfer(connection);
        } else if (data.type === 'metadata-ack') {
            const { transferId } = data.payload;
            console.log(`Metadata acknowledged for ${transferId}, starting chunking.`);
            const worker = Array.from(workersRef.current.values()).find(w => w.id === transferId);
            
            setFileTransfers(prevFileTransfers => {
                const transfer = prevFileTransfers.find(ft => ft.id === transferId);
                if (worker && transfer) {
                    sendChunk(worker, transfer, connection);
                }
                return prevFileTransfers;
            });

        } else if (data.type === 'chunk-ack') {
            const { transferId } = data.payload;
            console.log(`Chunk acknowledged for ${transferId}, starting next transfer.`);
            const worker = Array.from(workersRef.current.values()).find(w => w.id === transferId);
            if (worker) {
                worker.isBusy = false;
                worker.chunkOffset = 0;
                worker.lastChunkTime = null;
                startNextTransfer(connection);
            }
        } else if (data.type === 'transfer-decline') {
            console.log('Receiver declined transfer.');
            setIsTransferring(false);
            fileTransfers.forEach(ft => {
                if (ft.status === 'queued') {
                    updateTransferState(ft.id, { status: 'failed', error: 'Receiver declined transfer' });
                }
            });
        }
    };

    const sendFiles = () => {
        if (!conn || fileTransfers.length === 0) return;
        setIsTransferring(true);

        transferQueueRef.current = fileTransfers.map(ft => ft.id);
        workersRef.current.clear();
        for (let i = 0; i < MAX_WORKERS; i++) {
            workersRef.current.set(`worker-${i}`, { id: `worker-${i}`, isBusy: false, chunkOffset: 0, lastChunkTime: null });
        }

        const transferRequest = {
            type: 'transfer-request',
            payload: {
                fileCount: fileTransfers.length,
                totalSize: fileTransfers.reduce((sum, t) => sum + t.file.size, 0),
                files: fileTransfers.map(t => ({ name: t.file.name, size: t.file.size, type: t.file.type })),
            },
        };
        conn.send(transferRequest);
    };

    const cancelTransfer = () => {
        if (conn) conn.close();
        setIsTransferring(false);
        setFileTransfers(prev =>
            prev.map(ft =>
                ft.status === 'transferring' || ft.status === 'queued'
                    ? { ...ft, status: 'failed', error: 'Transfer cancelled' }
                    : ft
            )
        );
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setFiles(prev => [...prev, ...Array.from(e.target.files!)]);
        }
    };

    const removeFile = (index: number) => {
        const removedFile = files[index];
        setFiles(files.filter((_, i) => i !== index));
        setFileTransfers(prev => prev.filter(ft => !(ft.file.name === removedFile.name && ft.file.size === removedFile.size)));
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
    };

    const formatSpeed = (bytesPerSecond: number) => `${formatFileSize(bytesPerSecond)}/s`;

    const formatTime = (seconds: number) => {
        if (seconds === Infinity || isNaN(seconds)) return '--:--';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div className="container">
            <button className="back-button" onClick={goBack}><i className="fas fa-arrow-left"></i> Back</button>
            <h2>Sender</h2>
            {conn ? (
                <div>
                    <div className="status-container">
                        <div className={`status-dot ${connectionStatus}`} />
                        <p>
                            {connectionStatus === 'connecting' && 'Connecting...'}
                            {connectionStatus === 'connected' && 'Connected to peer'}
                            {connectionStatus === 'disconnected' && 'Disconnected'}
                        </p>
                    </div>
                    {isTransferring ? (
                        <div className="transfer-progress">
                            <div className="overall-progress">
                                <h3>Overall Progress: {overallProgress.completed} of {overallProgress.total} files</h3>
                                <progress className="progress-bar overall" value={overallProgress.transferredBytes} max={overallProgress.totalBytes}></progress>
                                <p>{((overallProgress.transferredBytes / overallProgress.totalBytes) * 100 || 0).toFixed(2)}%</p>
                                <p>{formatFileSize(overallProgress.transferredBytes)} / {formatFileSize(overallProgress.totalBytes)}</p>
                            </div>
                            <div className="file-transfer-list">
                                <h4>File Transfer Status:</h4>
                                {fileTransfers.map((transfer, index) => (
                                    <div key={transfer.id} className={`file-transfer-item ${transfer.status}`}>
                                        <div className="file-info">
                                            <span className="file-name">{index + 1}. {transfer.file.name}</span>
                                            <span className="file-size">({formatFileSize(transfer.file.size)})</span>
                                        </div>
                                        <div className="transfer-details">
                                            <div className="status-indicator">
                                                <span className={`status-badge ${transfer.status}`}>
                                                    {transfer.status.charAt(0).toUpperCase() + transfer.status.slice(1)}
                                                </span>
                                            </div>
                                            {transfer.status === 'transferring' && (
                                                <>
                                                    <progress className="progress-bar file" value={transfer.progress} max="100"></progress>
                                                    <div className="transfer-stats">
                                                        <span>{transfer.progress}%</span>
                                                        <span>{formatSpeed(transfer.speed)}</span>
                                                        <span>ETA: {formatTime(transfer.eta)}</span>
                                                    </div>
                                                </>
                                            )}
                                            {transfer.status === 'failed' && transfer.error && (
                                                <div className="error-message"><span>Error: {transfer.error}</span></div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <button className="btn btn-danger" onClick={cancelTransfer}>Cancel Transfer</button>
                        </div>
                    ) : (
                        <div className="form-container">
                            <input type="file" multiple ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileChange} disabled={connectionStatus !== 'connected'} />
                            <button className="btn" onClick={() => fileInputRef.current?.click()} disabled={connectionStatus !== 'connected'}>
                                {files.length > 0 ? 'Add More Files' : 'Choose Files'}
                            </button>
                            {files.length > 0 && (
                                <div className="file-list">
                                    <ul>
                                        {files.map((file, index) => (
                                            <li key={index}>
                                                {file.name}
                                                <button onClick={() => removeFile(index)}>×</button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <button onClick={sendFiles} className="btn" disabled={files.length === 0 || connectionStatus !== 'connected'}>Send Files</button>
                        </div>
                    )}
                </div>
            ) : (
                <div className="form-container">
                    <form onSubmit={(e) => { e.preventDefault(); if (manualPeerId.trim()) connectToPeer(manualPeerId.trim()); }} className="form-container">
                        <input type="text" value={manualPeerId} onChange={(e) => setManualPeerId(e.target.value)} placeholder="Enter receiver's ID" className="form-input" />
                        <button type="submit" className="btn">Connect</button>
                    </form>
                    <div className="qr-container">
                        <h3>or Scan QR Code</h3>
                        <Scanner onScan={(result) => connectToPeer(result[0].rawValue)} onError={(error) => {
                            if (error instanceof Error) {
                                console.log(error.message);
                            }
                        }} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default Sender;
